import React, { useEffect, useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { Link } from "gatsby"
import { useAnimation, motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { useKeenSlider } from "keen-slider/react"
import "keen-slider/keen-slider.min.css"

const slideshow = ({ block }) => {
  const { heading, text, width, autoplay, btnLink, btnLabel, slideshowItem } = block
  const controls = useAnimation()
  const [ref, inView] = useInView({
    /* Optional options */
    threshold: 0.35,
  })

  useEffect(() => {
    if (inView) {
      controls.start("visible")
    }
  }, [controls, inView])

  const animation = { duration: 20000, easing: (t) => t }
  
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
      slides: {
        perView: 1,
        spacing: 16,
      },
      breakpoints: {
        "(min-width: 640px)": {
          loop: true,
          slides: { perView: 1, 
            spacing: 16,
            origin: "center",
          },
        },
      },
      initial: 0,
      created() {
        setLoaded(true)
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel)
      },
    },
    [
      (slider) => {
        let timeout
        let mouseOver = false
        function clearNextTimeout() {
          clearTimeout(timeout)
        }
        function nextTimeout() {
          clearTimeout(timeout)
          if (mouseOver) return
          timeout = setTimeout(() => {
            if (autoplay) {
              slider.next()
            }
          },2000)
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true
            clearNextTimeout()
          })
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false
            nextTimeout()
          })
          nextTimeout()
        })
        slider.on("dragStarted", clearNextTimeout)
        slider.on("animationEnded", nextTimeout)
        slider.on("updated", nextTimeout)
      }
    ]
  )

  return (
    <motion.section
      className="section module-image-text-carousel"
      ref={ref}
      animate={controls}
      initial="hidden"
      variants={motionSection}
    >
      <div className="">
        
        <div className="module-image-text-carousel__content flex flex-col justify-center mb-8 items-center text-center container max-w-screen-md mx-auto px-6">
          <h2 className="w-fit h2 mb-6 relative">
            <motion.div variants={title}>{heading}</motion.div>
            <motion.div
              variants={underline}
              className="section-heading__underline bg-orange-400 h-2 w-full absolute -bottom-4 left-0"
            />
          </h2>
          <motion.p variants={desc}>{text}</motion.p>
        </div>

        <motion.div
          variants={title}
          // className="relative container "
          className={width ? "relative" : "relative container max-w-screen-xl" }
        >
          <div className="navigation-wrapper">
            <div ref={sliderRef} className={"keen-slider h-[70vh] " + (width ? "" : "rounded-lg")}>
              {slideshowItem.map((card, index) => (
                <div className={"keen-slider__slide overflow-hidden relative " + (width ? "" : "rounded-lg")}>
                  <motion.div variants={motionCarouselImg} className="h-full w-full">
                    <GatsbyImage
                      image={card.image.asset.gatsbyImageData}
                      alt={card.image.asset.altText}
                      className="w-full h-full object-cover after:content-[''] after:absolute after:inset-0 after:bg-black/40"
                    />
                  </motion.div>

                  <motion.div className="absolute inset-0 p-4 flex items-center justify-center">
                      <div className="max-w-sm text-center text-white">
                        <h3 className="h3 underline decoration-[5px] underline-offset-8 decoration-orange-400">{card.heading}</h3>
                        <p className="mt-4 text-orange-100">{card.text}</p>
                        {card.btnLabel && (
                          <Link to={card.btnLink.slug.current} className="btn btn-underline mt-2 inline-block">
                            {card.btnLabel}
                          </Link>
                        )}
                      </div>                    

                  </motion.div>
                  
                </div>
              ))}
            </div>
            {loaded && instanceRef.current && (
              <>
                <Arrow
                  left
                  onClick={e =>
                    e.stopPropagation() || instanceRef.current?.prev()
                  }
                  disabled={currentSlide === 0}
                />

                <Arrow
                  onClick={e =>
                    e.stopPropagation() || instanceRef.current?.next()
                  }
                  disabled={
                    currentSlide ===
                    instanceRef.current.track.details.slides.length - 1
                  }
                />
              </>
            )}
          </div>
          {loaded && instanceRef.current && (
            <div className="dots">
              {[
                ...Array(instanceRef.current.track.details.slides.length).keys(),
              ].map((idx) => {
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      instanceRef.current?.moveToIdx(idx)
                    }}
                    className={"dot" + (currentSlide === idx ? " active" : "")}
                  ></button>
                )
              })}
            </div>
          )}
          
        </motion.div>

        <div className="container text-center mt-6">
          {btnLabel && (
            <Link to={btnLink.slug.current} className="btn btn-primary">
              {btnLabel}
            </Link>
          )}
        </div>
      </div>
    </motion.section>
  )
}

export default slideshow

const motionSection = {
  hidden: {
    opacity: 0,
    // y: 100,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      staggerChildren: 0.15,
      staggerDirection: 1,
      // delay: 0.3,
      ease: [0.16, 1, 0.3, 1],
      // when: "beforeChildren",
    },
  },
}

const underline = {
  hidden: {
    // opacity: 0,
    width: 0,
  },
  visible: {
    opacity: 1,
    // right: 0,
    width: "100%",
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}
const title = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    // right: 0,
    y: 0,
    transition: {
      duration: 1.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}
const desc = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    // right: 0,
    y: 0,
    transition: {
      duration: 1.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}
const motionCarouselImg = {
  hidden: {
    scale: 1.1,
  },
  visible: {
    scale: 1,
    transition: {
      duration: 1.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}


function Arrow(props) {
  const disabeld = props.disabled ? " arrow--disabled" : ""
  return (
    <svg
      onClick={props.onClick}
      className={`arrow ${
        props.left ? "arrow--left" : "arrow--right"
      } ${disabeld}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      {props.left && (
        <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
      )}
      {!props.left && (
        <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
      )}
    </svg>
  )
}